import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";
import type { Asset, CitationTarget, Message, ReferenceItem, SourceTab } from "../../api/types.js";
import { CitedSourcesTab } from "./CitedSourcesTab.js";
import { ConversationAssetsTab } from "./ConversationAssetsTab.js";
import { ReferencePoolTab } from "./ReferencePoolTab.js";
import { SourceTabs } from "./SourceTabs.js";
import { citedSourcesForMessage } from "./citationSources.js";

export function SourceDrawer({
  open,
  tab,
  setTab,
  onClose,
  currentMessage,
  citationSources,
  referencePool,
  referencePoolTotalCount,
  referencePoolNextCursor,
  referencePoolLoading,
  referencePoolLoaded,
  referencePoolError,
  assets,
  currentSessionId,
  citationTarget,
  loadReferencePoolPage,
  saveSourceAsAsset
}: {
  open: boolean;
  tab: SourceTab;
  setTab: (tab: SourceTab) => void;
  onClose: () => void;
  currentMessage: Message | null;
  citationSources: ReferenceItem[];
  referencePool: ReferenceItem[];
  referencePoolTotalCount: number;
  referencePoolNextCursor: string | null;
  referencePoolLoading: boolean;
  referencePoolLoaded: boolean;
  referencePoolError: string;
  assets: Asset[];
  currentSessionId?: string | null;
  citationTarget: CitationTarget | null;
  loadReferencePoolPage: (reset?: boolean) => Promise<void>;
  saveSourceAsAsset: (source: ReferenceItem) => void | Promise<void>;
}) {
  const sourceListRef = useRef<HTMLDivElement | null>(null);
  const cited = useMemo(() => citedSourcesForMessage(currentMessage, citationSources), [currentMessage, citationSources]);
  const selectedCitation = citationTarget && citationTarget.messageId === currentMessage?.id ? citationTarget.localIndex : null;
  const missingSelectedCitation = selectedCitation !== null
    && !cited.some((source) => Number(source.citationIndex || source.index) === selectedCitation);

  useEffect(() => {
    if (!open || tab !== "pool" || referencePoolLoaded || referencePoolLoading || referencePoolError) return;
    void loadReferencePoolPage(true);
  }, [open, tab, referencePoolLoaded, referencePoolLoading, referencePoolError, loadReferencePoolPage]);

  useLayoutEffect(() => {
    if (!open || tab !== "cited" || selectedCitation === null || missingSelectedCitation) return;
    const list = sourceListRef.current;
    const card = list?.querySelector<HTMLElement>(`[data-source-citation-index="${selectedCitation}"]`);
    if (!list || !card) return;
    const listRect = list.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    list.scrollTo({ top: Math.max(0, list.scrollTop + cardRect.top - listRect.top - 8), behavior: "auto" });
    card.focus({ preventScroll: true });
  }, [open, tab, selectedCitation, missingSelectedCitation, citationTarget?.navigationRequestId, cited.length]);

  return (
    <aside id="sourceDrawer" className={`source-drawer ${open ? "open" : ""}`} aria-label="引用资料">
      <div className="panel-head">
        <div>
          <strong>引用资料</strong>
          <span>({tab === "assets" ? assets.length : tab === "cited" ? cited.length : referencePoolTotalCount})</span>
        </div>
        <button className="icon-button" type="button" aria-label="关闭引用资料" onClick={onClose}><X size={16} /></button>
      </div>
      <SourceTabs tab={tab} setTab={setTab} />
      <div className="source-list" ref={sourceListRef}>
        {tab === "cited" && <CitedSourcesTab sources={cited} selectedCitation={selectedCitation} missingSelectedCitation={missingSelectedCitation} currentSessionId={currentSessionId} saveSourceAsAsset={saveSourceAsAsset} />}
        {tab === "pool" && <ReferencePoolTab
          referencePool={referencePool}
          loading={referencePoolLoading}
          loaded={referencePoolLoaded}
          error={referencePoolError}
          hasMore={Boolean(referencePoolNextCursor) && !referencePoolError}
          onLoadMore={() => { void loadReferencePoolPage(false); }}
          onRetry={() => { void loadReferencePoolPage(!referencePoolLoaded); }}
          currentSessionId={currentSessionId}
          saveSourceAsAsset={saveSourceAsAsset}
        />}
        {tab === "assets" && <ConversationAssetsTab assets={assets} currentSessionId={currentSessionId} saveSourceAsAsset={saveSourceAsAsset} />}
      </div>
    </aside>
  );
}
